import { User } from "@easycv/common/types/user";
import { TemplateList } from "@easycv/common/types/util";
import { getHomeSetup } from "../../customSections/index.js";
import {
  mergeItems,
  previewSection,
  previewSections
} from "../../lib/formatUtilities.js";
import { escapeHtml } from "../../lib/frontendUtilities.js";
import { anyPathOverlap } from "../../lib/hierarchyUtilities.js";
import { cloneWithFunctions, getField } from "../../lib/utilities.js";
import hopkins, {
  hopkinsLogoOutput,
  hopkinsSettingsSetup
} from "../hopkins.js";

// Clone template
const hopkinsUniversity = cloneWithFunctions(hopkins);
// Empty hopkins input
hopkinsUniversity.input = [];

// Filter hopkins anesthesia users
hopkinsUniversity.filter = function (
  cvOwner: User,
  templateList: TemplateList
) {
  return anyPathOverlap(
    templateList["hopkins_university"]?.paths || [],
    cvOwner.requestedPaths
  );
};

// Name template
hopkinsUniversity.Title = "Hopkins University Output DO NOT MAP";
hopkinsUniversity.template =
  "/templateFiles/hopkins_university_template_v1_0.docx";

// Adjust font and disable section entry count
hopkinsUniversity.outputSettings.font = "Calibri";
hopkinsUniversity.outputSettings.fontSize = 11;

// Removes Links to PMID option from download modal
hopkinsUniversity.customOptions = [
  {
    field: "linkPMIDs",
    name: "",
    default: false,
    options: []
  }
];

const hopkinsLogos = {
  jhu: "John Hopkins University",
  apl: "Applied Physics Laboratory",
  cbs: "Carey Business School",
  soe: "School of Education",
  ksas: "Krieger School of Arts & Sciences",
  pbd: "Peabody Institute",
  sais: "School of Advanced International Studies",
  wse: "Whiting School of Engineering"
};

// Define the settings tab
hopkinsUniversity.input[0] = {
  field: "overview",
  icon: "home",
  type: "custom",
  title: "Settings",
  setup: getHomeSetup({
    citationNames: true,
    noCitationPrefs: true,
    noFirstName: true,
    noPubmedImport: true,
    initialSetup: hopkinsSettingsSetup(hopkinsLogos)
  })
};

// Create a section tag for every logo
Object.keys(hopkinsLogos).forEach((school) => {
  hopkinsUniversity.output[school] = {
    toggle: hopkinsLogoOutput(school)
  };
});

//adjust personal data
hopkinsUniversity.output["bio"].map["bio_linkedIn"] = "linkedIn";
hopkinsUniversity.output["bio"].map["bio_facebook"] = "facebook";
hopkinsUniversity.output["bio"].map["bio_instagram"] = "instagram";
hopkinsUniversity.output["bio"].map["bio_bluesky"] = "bluesky";
hopkinsUniversity.output["bio"].map["bio_myStrengths"] = "myStrengths";

// adjust sections in postDoctoralTraining
hopkinsUniversity.output["pdt"].sections = [
  { type: "fellowship", title: "Post-Doctoral Training" },
  { type: "other", title: "Other" }
];

//adjust publications
hopkinsUniversity.output["osch"]["sectionsArray"] = [
  { type: "memoirs", title: "Memoirs [M]" },
  { type: "creativeWriting", title: "Creative Writing [CW]" },
  {
    type: "methods",
    title: "Methods and Techniques"
  },
  {
    type: "commentaries",
    title: "Opinions, Perspectives, Political Commentary, Advocacy, Essays [OP]"
  },
  {
    type: "Proceedings of meetings or other non-peer reviewed scholarship",
    title: "Original Research, Not Peer Reviewed"
  },
  { type: "proceedingsReports", title: "Proceedings Reports [PR]" },
  {
    type: "Professional educational materials or reports, in print or other media",
    title:
      "Published Curricula, Learner Assessment Tools, Educational Evaluations"
  },
  { type: "policy", title: "White Papers [WP]" },
  { type: "interviews", title: "Media Releases or Interviews [MR]" },
  {
    type: "media",
    title: "Other Media [OM] (Videos, Websites, Blogs, Social Media, etc.)"
  }
];

function updateTextToJHU(
  outputKey: string,
  extraTypeFilter?: string,
  extraProps: object = {}
) {
  hopkinsUniversity.output[outputKey]["sectionsArray"] =
    hopkinsUniversity.output[outputKey]["sectionsArray"]
      .filter((item) => !["Uncategorized", "JHMI"].includes(item.title))
      .toSpliced(
        0,
        0,
        {
          ...extraProps,
          title: "Uncategorized",
          filter: (n) =>
            ["JHU", "Regional", "National", "International"].indexOf(n.level) <
              0 && (extraTypeFilter ? n.type == extraTypeFilter : true)
        },
        {
          ...extraProps,
          title: "JHU",
          filter: (n) =>
            n.level == "JHU" &&
            (extraTypeFilter ? n.type == extraTypeFilter : true)
        }
      );
}
// Need to go through these sections, change "JHMI" title to "JHU", which also forces us to readd the "Uncategorized" because it filters based on that "JHMI" title.
updateTextToJHU("tch1", "Teaching of Students in Courses");
updateTextToJHU("tch4", "external");
updateTextToJHU("ment2", "students");
updateTextToJHU("eda1", "assessment");
updateTextToJHU("eda2", "curriculum");
updateTextToJHU("eda3", "leadership");
updateTextToJHU("co", undefined, { type: "conference" });

// This section also needs a similar change to above, but doesn't have "Uncategorized" so it is more simple
hopkinsUniversity.output["it"]["sectionsArray"] = hopkinsUniversity.output[
  "it"
]["sectionsArray"].map((section) => {
  return {
    ...section,
    title: section.title === "JHMI" ? "JHU" : section.title
  };
});

//adjust Mentees
hopkinsUniversity.output["ment"].format = {
  t: ["fullMonthDayYear"],
  1: [
    mergeItems([
      { key: "title" },
      { key: "position" },
      { key: "secondary" },
      { key: "description" },
      { key: "workTime" },
      { key: "role" }
    ]),
    "comments"
  ]
};

// Professional Experience output section toggle (Make sure PE always shows up when something is there)
hopkinsUniversity.output["prexSection"].toggle = function (
  cvDef,
  filter,
  { customExportSettings }
) {
  var emptySections =
    typeof customExportSettings != "undefined" &&
    customExportSettings.emptySections != null &&
    !!customExportSettings.emptySections;
  return (
    emptySections ||
    getField(cvDef, "facultyAppointments")
      .filter(filter)
      .concat(getField(cvDef, "hospitalAppointments").filter(filter))
      .concat(getField(cvDef, "otherPositions").filter(filter)).length > 0
  );
};

// Professional Experience output format
hopkinsUniversity.output["pe"].format = {
  t: ["fullMonthDayYear"],
  1: [mergeItems([{ key: "title" }, { key: "location" }])]
};

//adjust Systems Innovation
hopkinsUniversity.output["siqi"]["sectionsArray"] = [
  {
    type: "internal",
    title: "System Innovation efforts inside JHU"
  },
  {
    type: "external",
    title: "System Innovation efforts outside of JHU"
  },
  {
    type: "QIleadership",
    title: "System Innovation Program Building/Leadership"
  }
];

// Add new output objects here
// Academic Focus
hopkinsUniversity.output["academic_focus"] = {
  field: "researchInterests",
  xml: true,
  map: {
    ac_academicFocus: "clinicalFocus"
  }
};

hopkinsUniversity.output["ra"].format["1"] = [
  mergeItems([
    { key: "role" },
    { key: "title" },
    { key: "source" },
    { key: "location" }
  ]),
  "comments"
];

// Certification
hopkinsUniversity.output["cert"] = {
  section: "certification",
  type: "certification",
  field: "clinicalActivityAndInnovations",
  preface: true,
  format: {
    t: ["fullMonthDayYear"],
    1: ["location"],
    2: ["number"],
    3: ["comments"]
  }
};

// Community, Humanitarian, Philanthropic services
hopkinsUniversity.output["chp"].sectionsArray = [
  { type: "Activities", title: "Community Service", totalSuffix: " Total" },
  {
    type: "humanitarian",
    title: "Humanitarian Activities",
    totalSuffix: " Total"
  },
  {
    type: "philanthropy",
    title: "Philanthropic Activities",
    totalSuffix: " Total"
  }
];

const previewBioItem = function (key, label) {
  return function (def) {
    return def[key] == null
      ? null
      : "&emsp;&emsp;&emsp;&emsp;" +
          label +
          "&emsp;" +
          escapeHtml(def[key] || "") +
          "<br>";
  };
};

let hopkinsUniversityPreview = [
  '<h4 style="text-align: center;">Curriculum Vitae</h4>',
  '<h4 style="text-align: center;">The Johns Hopkins University School of Medicine</h4>',
  "<br>",
  function (def) {
    return def.genDate == null
      ? null
      : '<h4 style="text-align: center;">' +
          escapeHtml(def.bio_name || "") +
          "&emsp;&emsp;&emsp;&emsp;" +
          escapeHtml(def.genDate || "") +
          "</h4><br>";
  },
  "<br>",
  '<h4 style="text-align: left;"><b>DEMOGRAPHIC AND PERSONAL INFORMATION</b></h4>',
  '<h4 style="text-align: left;">Current Appointments</h4>',
  previewSection("caua", "University", ["t", "1"]),
  previewSection("caha", "Hospital", ["t", "1"]),
  previewSection("caoa", "Other", ["t", "1"]),
  "<br>",
  "<h4>Personal Data</h4>",
  previewBioItem("bio_division", ""),
  previewBioItem("bio_department", ""),
  previewBioItem("bio_officeAddress", ""),
  previewBioItem("bio_officeLocation", ""),
  previewBioItem("bio_workPhone", "&emsp;Tel"),
  previewBioItem("bio_workFax", "&emsp;Fax"),
  previewBioItem("bio_workEmail", "&emsp;E-Mail"),
  previewBioItem("bio_linkedIn", "&emsp;LinkedIn"),
  previewBioItem("bio_facebook", "&emsp;Facebook"),
  previewBioItem("bio_instagram", "&emsp;Instagram"),
  previewBioItem("bio_bluesky", "&emsp;Bluesky"),
  "<br>",
  '<h4">Education and Training</h4>',
  function (def) {
    return def.eduPre == null
      ? null
      : "<p>" + escapeHtml(def.eduPre || "") + "</p>";
  },
  previewSections("eduSections", ["t", "1"]),
  previewSections("pdtSections", ["t", "1"]),
  "<br>",
  previewSection("pe", '<h4">Professional Experience or Employment</h4>', [
    "t",
    "1"
  ]),
  "<br>",
  "<h4><b>PUBLICATIONS</b></h4>",
  function (def) {
    return def.schPre == null
      ? null
      : "<p>" + escapeHtml(def.schPre || "") + "</p>";
  },
  previewSections("schSections", ["1", "2"]),
  previewSections("oschSections", ["1", "2"]),
  "<br>",
  "<h4><b>FUNDING</b></h4>",
  "<h4>EXTRAMURAL Funding</h4>",
  "<p>Research Extramural Funding</p>",
  previewSections("frextSections", ["t", "1"]),
  "<p>Educational Extramural Funding</p>",
  previewSections("feextSections", ["t", "1"]),
  "<p>System Innovation or Quality Improvement Extramural Funding</p>",
  previewSections("fsiextSections", ["t", "1"]),
  "<p>Other Extramural Funding</p>",
  previewSections("foextSections", ["t", "1"]),
  "<h4>INTRAMURAL Funding</h4>",
  "<p>Research Intramural Funding</p>",
  previewSections("frintSections", ["t", "1"]),
  "<p>Educational Intramural Funding</p>",
  previewSections("feintSections", ["t", "1"]),
  "<p>System Innovation or Quality Improvement Intramural Funding</p>",
  previewSections("fsiintSections", ["t", "1"]),
  "<p>Other Intramural Funding</p>",
  previewSections("fointSections", ["t", "1"]),
  "<br>",
  "<h4><b>ACTIVITIES</b></h4>",
  function (def) {
    return def.ed_educationalFocus == null
      ? null
      : "<h4>Academic Focus</h4><p>" +
          escapeHtml(def.ac_academicFocus || "", { allowTags: true }) +
          "</p><br>";
  },
  previewSection("cert", "<h4>Certification</h4>", ["t", "1", "2", "3"]),
  "<br>",
  "<h4><b>EDUCATIONAL ACTIVITIES</b></h4>",
  function (def) {
    return def.ed_educationalFocus == null
      ? null
      : "<h4>Educational Focus</h4><p>" +
          escapeHtml(def.ed_educationalFocus || "", { allowTags: true }) +
          "</p><br>";
  },
  "<h4>Teaching and Facilitating Learning</h4>",
  "<p>Primary Classroom instruction</p>",
  previewSections("tch1Sections", ["t", "1"]),
  "<p>Workshops / seminars</p>",
  previewSections("tch4Sections", ["t", "1"]),
  previewSection("pst", "<p>Posters</p>", ["t", "1"]),
  "<br>",
  "<h4>Mentoring, coaching, and advising</h4>",
  function (def) {
    return def.mentPre == null
      ? null
      : "<p>" + escapeHtml(def.mentPre || "") + "</p>";
  },
  previewSections("mentSections", ["t", "1"]),
  "<br>",
  "<p>Mentoring, coaching, and advising programs</p>",
  function (def) {
    return def.ment2Pre == null
      ? null
      : "<p>" + escapeHtml(def.ment2Pre || "") + "</p>";
  },
  previewSections("ment2Sections", ["t", "1"]),
  "<br>",
  "<h4>Assessment and Evaluation</h4>",
  previewSections("eda1Sections", ["t", "1"]),
  "<br>",
  "<h4>Program Building and Curriculum Development</h4>",
  previewSections("eda2Sections", ["t", "1"]),
  "<br>",
  "<h4>Educational Leadership</h4>",
  previewSections("eda3Sections", ["t", "1"]),
  "<br>",
  previewSection(
    "edda",
    "<h4>Educational Demonstration Activities to external audiences</h4>",
    ["t", "1"]
  ),
  "<h4><b>RESEARCH ACTIVITIES</b></h4>",
  function (def) {
    return def.ra_researchFocus == null
      ? null
      : "<p>Research Focus</p><p>" +
          escapeHtml(def.ra_researchFocus || "", { allowTags: true }) +
          "</p><br>";
  },
  previewSections("raSections", ["t", "1"]),
  "<br>",
  "<h4><b>SYSTEM INNOVATION AND QUALITY IMPROVEMENT ACTIVITIES</b></h4>",
  function (def) {
    return def.si_systemInnovationFocus == null
      ? null
      : "<p>System Innovation Focus</p><p>" +
          escapeHtml(def.si_systemInnovationFocus || "", {
            allowTags: true
          }) +
          "</p><br>";
  },
  previewSections("siqiSections", ["t", "1"]),
  "<h4><b>ORGANIZATIONAL ACTIVITIES</b></h4>",
  previewSection("iaa", "International Administrative Appointments", [
    "t",
    "1"
  ]),
  "<br>",
  "<h4>Editorial Activities</h4>",
  previewSections("eaSections", ["t", "1"]),
  previewSection("oaac", "Advisory Committees, Review Groups/Study Sections", [
    "t",
    "1"
  ]),
  previewSection("oaps", "Professional Societies", ["t", "1", "2"]),
  "<p>Conference Organizer</p>",
  previewSections("coSections", ["t", "1"]),
  previewSection("cs", "Consultantships", ["t", "1"]),
  "<br>",
  "<h4><b>HONORS</b></h4>",
  previewSection("ah", "Awards, Honors", ["t", "1"]),
  "<br>",
  "<p>Invited Talks</p>",
  previewSections("itSections", ["t", "1"]),
  previewSection("vp", "Visiting Professorships", ["t", "1"]),
  "<h4><b>OTHER PROFESSIONAL ACCOMPLISHMENTS</b></h4>",
  previewSection("pod", "Oral/Podium Presentations", ["t", "1"]),
  previewSection("mil", "Military Service", ["t", "1"]),
  previewSections("chpSections", ["t", "1"]),
  previewSection("oth", "Other", ["t", "1"])
];

hopkinsUniversity.preview = hopkinsUniversityPreview;

export default hopkinsUniversity;
